import { degreesToRadians } from 'cadenza/utils/math';

import type { Coordinate } from 'ol/coordinate';

/**
 * The parameters needed for drawing the image on the canvas context, please also look at
 * https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
 */
interface RectangleCanvasRenderingProperties {
  /* X coordinate top left */
  dx: number;
  /* Y coordinate top left */
  dy: number;
  /* height to draw the image */
  dHeight: number;
  /* width to draw the image */
  dWidth: number;
}

/**
 * The parameters needed for drawing the image on the canvas context, please also look at
 * https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/ellipse
 */
interface EllipseCanvasRenderingProperties {
  /* The eccentric angle in radians at which the ellipse starts, but because we always draw the complete ellipse, the startAngle always is 0 */
  startAngle: number;
  /* The eccentric angle in radians at which the ellipse ends, but because we always draw the complete ellipse, the endAngle always is 2*Pi */
  endAngle: number;
  /* The rotation of the ellipse, but because we rotate the canvas first, rotation always is 0 */
  rotation: number;
  /* The x-axis (horizontal) coordinate of the ellipse's center. */
  x: number;
  /* The y-axis (vertical) coordinate of the ellipse's center. */
  y: number;
  /* The ellipse's major-axis radius. */
  radiusX: number;
  /* The ellipse's minor-axis radius. */
  radiusY: number;
}

/**
 * Calculate the center of a rectangle with coordinates A, B, C, D, where only A, B and D need to be known.
 * The center is calculated via the formula
 * Center = A + (1/2*AB) + (1/2*AD)
 * where AB = B - A
 * and AD = D - A
 *
 * @param coordA Corner A of the rectangle as array [x, y]
 * @param coordB Corner B of the rectangle as array [x, y]
 * @param coordC Corner C of the rectangle as array [x, y]
 * @param coordD Corner D of the rectangle as array [x, y]
 */
export function calculateRectangleCenter (
  coordA: Coordinate,
  coordB: Coordinate,
  coordC: Coordinate,
  coordD: Coordinate): Coordinate {
  const ab = [ coordB[0] - coordA[0], coordB[1] - coordA[1] ];
  const ad = [ coordD[0] - coordA[0], coordD[1] - coordA[1] ];
  return [
    coordA[0] + (1 / 2 * ab[0]) + (1 / 2 * ad[0]),
    coordA[1] + (1 / 2 * ab[1]) + (1 / 2 * ad[1]) ];
}

/**
 * Returns the parameters needed for drawing the image on the canvas context.
 * The parameter names correspond to those used in {@link https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage}
 *
 * @param coordA Corner A of the (maybe rotated) rectangle A/B/C/D as array [x, y]
 * @param coordC Corner C of the (maybe rotated) rectangle A/B/C/D as array [x, y]
 * @param centerCoordinate Center of the rectangle A/B/C/D as array [x, y]
 * @param clockwiseRotationInDegree0To359 The number of degrees representing a clockwise rotation in the scale of 0..359
 * @param image The image to render on the canvas
 * @param keepAspect If false the aspect ratio is not kept
 */
export function getParametersForDrawingTheImageOnTheCanvasContext (
  coordA: Coordinate,
  coordC: Coordinate,
  centerCoordinate: Coordinate,
  clockwiseRotationInDegree0To359: number,
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  keepAspect: boolean): RectangleCanvasRenderingProperties {
  const unrotatedFrom = (clockwiseRotationInDegree0To359 > 0)
    ? getReverseRotatedCanvasCoordinate(coordA, centerCoordinate, clockwiseRotationInDegree0To359)
    : coordA;
  const unrotatedTo = (clockwiseRotationInDegree0To359 > 0)
    ? getReverseRotatedCanvasCoordinate(coordC, centerCoordinate, clockwiseRotationInDegree0To359)
    : coordC;

  const x1 = Math.round(unrotatedFrom[0]);
  const y1 = Math.round(unrotatedFrom[1]);
  const x2 = Math.round(unrotatedTo[0]);
  const y2 = Math.round(unrotatedTo[1]);

  const requestedWidth = Math.abs(x2 - x1);
  const requestedHeight = Math.abs(y2 - y1);
  const imageAspectRatio = image.width / image.height;
  const [ width, height ] = adjustExtentKeepingAspectRatio(requestedWidth, requestedHeight, imageAspectRatio);

  let dx, dy;
  if (keepAspect) {
    dx = Math.min(x1, x2) + (requestedWidth - width) / 2;
    dy = Math.min(y1, y2) + (requestedHeight - height) / 2;
  } else {
    dx = Math.min(x1, x2);
    dy = Math.min(y1, y2);
  }

  const dWidth = keepAspect ? width : requestedWidth;
  const dHeight = keepAspect ? height : requestedHeight;
  return { dx, dy, dWidth, dHeight };
}

/**
 * Returns the parameters needed for drawing the oval as ellipse on the canvas context.
 *
 * @param center The center coordinate of the oval
 * @param corner The virtual "top right" corner coordinate of the oval
 */
export function getParametersForDrawingTheOvalOnTheCanvasContext (
  center: Coordinate,
  corner: Coordinate): EllipseCanvasRenderingProperties {
  const x = center[0];
  const y = center[1];
  const rotation = 0;
  const startAngle = 0;
  const endAngle = 2 * Math.PI;
  const radiusX = Math.abs(corner[0] - center[0]);
  const radiusY = Math.abs(corner[1] - center[1]);
  return { x, y, radiusX, radiusY, rotation, startAngle, endAngle };
}

/**
 * Get the reverse rotated/unrotated coordinate by giving the center and the clockwise rotation angle in the scale 0..359
 *
 * @param rotatedCoordinate The rotated coordinate which the unrotated coordinate is asked for
 * @param center The center coordinate which is used to unrotate the coordinate
 * @param clockwiseRotationInDegree0To359 The number of degrees representing a clockwise rotation in the scale of 0..359
 */
function getReverseRotatedCanvasCoordinate (
  rotatedCoordinate: Coordinate,
  center: Coordinate,
  clockwiseRotationInDegree0To359: number): Coordinate {
  const [ rotatedX, rotatedY ] = rotatedCoordinate;
  const [ centerX, centerY ] = center;
  const radians = -(Math.PI / 180) * clockwiseRotationInDegree0To359;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const reverseRotatedPointX = (cos * (rotatedX - centerX)) - (sin * (rotatedY - centerY)) + centerX;
  const reverseRotatedPointY = (sin * (rotatedX - centerX)) + (cos * (rotatedY - centerY)) + centerY;
  return [ reverseRotatedPointX, reverseRotatedPointY ];
}

/**
 * Adapt the requestedWidth and requestedHeight to the given aspectRatio
 *
 * @param requestedWidth The requested width in pixels to draw on the canvas
 * @param requestedHeight The requested width in pixels to draw on the canvas
 * @param aspectRatio The aspect ratio
 */
function adjustExtentKeepingAspectRatio (
  requestedWidth: number,
  requestedHeight: number,
  aspectRatio: number): [number, number] {
  const requestedAspectRatio = requestedWidth / requestedHeight;
  if (requestedAspectRatio > aspectRatio) {
    return [ requestedHeight * aspectRatio, requestedHeight ];
  }
  return [ requestedWidth, requestedWidth / aspectRatio ];
}

/**
 * Reverse rotate the CanvasRenderingContext2D around a center coordinate by an amount of degrees measured clockwise
 *
 * @param context The CanvasRenderingContext2D interface, part of the Canvas API
 * @param centerCoordinate The center coordinate as an array [x, y]
 * @param clockwiseRotationInDegree0To359 The number of degrees representing a clockwise rotation in the scale of 0..359
 */
export function unrotateCanvasContextToDrawItemUnrotated (
  context: CanvasRenderingContext2D,
  centerCoordinate: Coordinate,
  clockwiseRotationInDegree0To359: number): void {
  context.translate(centerCoordinate[0], centerCoordinate[1]);
  context.rotate(degreesToRadians(clockwiseRotationInDegree0To359));
  context.translate(-centerCoordinate[0], -centerCoordinate[1]);
}

/**
 * Rotate the CanvasRenderingContext2D around a center coordinate by an amount of degrees measured clockwise
 *
 * @param context The CanvasRenderingContext2D interface, part of the Canvas API
 * @param centerCoordinate The center coordinate as an array [x, y]
 * @param clockwiseRotationInDegree0To359 The number of degrees representing a clockwise rotation in the scale of 0..359
 */
export function rotateCanvasContextIfItemToDrawIsRotated (
  context: CanvasRenderingContext2D,
  centerCoordinate: Coordinate,
  clockwiseRotationInDegree0To359: number): void {
  context.translate(centerCoordinate[0], centerCoordinate[1]);
  context.rotate(-degreesToRadians(clockwiseRotationInDegree0To359));
  context.translate(-centerCoordinate[0], -centerCoordinate[1]);
}
